import { watch } from 'vue';
import { useUserStore } from '~/stores/user';

/**
 * Owns "the signed-in user has an avatar" at app level.
 *
 * This used to be the responsibility of whichever page happened to mount:
 * pages/profile/[id].vue was the only place that called generateAvatar, and
 * pages/index.vue overwrote userProfile from a raw select without generating
 * anything. So the navbar rendered with a bare initials fallback and the real
 * avatar only appeared once a page had finished loading — or, for a new Google
 * user, not until they visited their own profile.
 *
 * Watching the store instead of hooking a route means every auth path is
 * covered — password login's hard reload, the OAuth redirect, a guest session,
 * and plain session restore — regardless of which route the user lands on.
 *
 * Fire and forget: ensureAvatar does a canvas encode, a storage upload and a
 * profiles UPDATE, and nothing on the render path should wait for that. The
 * navbar picks up the new URL reactively when it lands.
 */
export default defineNuxtPlugin((nuxtApp) => {
  // Pass the pinia instance explicitly rather than relying on the active one:
  // plugins run outside a component setup, so there is no ambient instance to
  // fall back on if module plugin ordering ever changes.
  const userStore = useUserStore(nuxtApp.$pinia);

  watch(
    () => userStore.userProfile?.id,
    (id) => {
      if (!id) return;
      // runWithContext because this watcher lives on the plugin, not a
      // component: when it fires there may be no active Nuxt instance, and
      // ensureAvatar reaches useSupabaseClient() -> useNuxtApp(), which throws
      // without one.
      nuxtApp.runWithContext(() => userStore.ensureAvatar());
    },
    { immediate: true }
  );
});
